import { CommonModule } from '@angular/common';
import { Component, inject, Input, NgZone, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { BrowserModule } from '@angular/platform-browser';
import { timeValidator2 } from '../../../../../common/time-array.validator2';
import { TimeRange } from '../schedule-time-range-chart/schedule-time-range-chart.component';
import { MatDialog } from '@angular/material/dialog';
import { TimeRangeDialogComponent } from '../time-range-dialog/time-range-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { SchedulerService } from '../../../../services/scheduler/scheduler.service';

@Component({
  selector: 'time-range-selector',
  imports: [CommonModule, MatSliderModule, MatTooltipModule, BrowserModule, MatFormFieldModule, ReactiveFormsModule, MatInputModule, FormsModule, MatIconModule, MatChipsModule, MatButtonModule, MatButtonToggleModule, MatCardModule, MatSelectModule],
  templateUrl: './time-range-selector.component.html',
  styleUrl: './time-range-selector.component.css'
})
export class TimeRangeSelectorComponent implements OnInit {
  @Input() timeRangeForm: FormGroup = new FormGroup([]);
  scheduleServer : SchedulerService = inject(SchedulerService);
  
  dialog: MatDialog = inject(MatDialog);
  zone: NgZone = inject(NgZone);
 
  timeRanges : FormArray<any> = new FormArray<any>([], timeValidator2());
  sliderStep : number = 1;

  predefinedRanges = [
    { label: 'Overnight', from: 0, to: 480 },         // 00:00 – 08:00
    { label: 'Morning', from: 480, to: 720 },         // 08:00 – 12:00
    { label: 'Afternoon', from: 780, to: 1020 },      // 13:00 – 17:00
    { label: 'Evening', from: 1080, to: 1320 },       // 18:00 – 22:00
    { label: 'Night Shift', from: 1320, to: 1439 }    // 22:00 – 23:59
  ];

  ngOnInit(): void {
    this.timeRangeForm.addControl('timeRanges', this.timeRanges);
  }

  // Converts minutes to HH:mm format
  formatTime(minutes: number): string {
    return this.scheduleServer.minutesToTime(minutes);
  }



  addTimeRange() {
    const timeRangesArray = this.timeRanges.value; // Get existing time ranges
    const step = 60; // 1 hour increment
    const maxTime = 1439; // Max time in minutes (23:59)
    const duration = step; // Fixed 1-hour duration
  
    // Sort ranges by start time
    timeRangesArray.sort((a: any, b: any) => a.fromTime - b.fromTime);
  
    let newStart = 0;
    let newEnd = duration;
  
    // Find the next available 1-hour gap
    for (let i = 0; i < timeRangesArray.length; i++) {
      const currentEnd = timeRangesArray[i].toTime;
      const nextStart = i + 1 < timeRangesArray.length ? timeRangesArray[i + 1].fromTime : maxTime;
  
      if (nextStart - currentEnd >= duration) {
        newStart = currentEnd;
        newEnd = newStart + duration;
        break;
      }
    }
  
    // If no gap found, try placing it at the end of the last range
    if (newEnd > maxTime) {
      newEnd = maxTime;
      newStart = maxTime - duration;
    }
  
    // Prevent adding if no available 1-hour slot is found
    if (newEnd - newStart < duration) {
      alert('No available 1-hour slot. Adjust existing ranges or remove one.');
      return;
    }
  
    // Add the new time range
    this.timeRanges.push(
      new FormGroup({ 
          fromTime: new FormControl(newStart), 
          toTime: new FormControl(newEnd)
        }
      )
    );
  
  }

  // Removes a time range
  removeTimeRange(index: number) {
    this.timeRanges.removeAt(index);
  }


  getTimeRanges() : TimeRange[] {
    let ranges : TimeRange[] = [];
    if (this.timeRanges.controls.length > 0) {
      ranges = this.timeRanges.controls.map(g => new TimeRange(
        g.get('fromTime')?.value == "" || g.get('fromTime')?.value == undefined || !g.get('fromTime')?.valid ? '00:00' : this.scheduleServer.minutesToTime24(g.get('fromTime')?.value), 
        g.get('toTime')?.value == "" || g.get('toTime')?.value == undefined  || !g.get('toTime')?.valid ? '23:59' : this.scheduleServer.minutesToTime24(g.get('toTime')?.value)));
    }

    return ranges;
  }

  editTimeRange(index: number) {
    const timeRange = this.timeRanges.at(index).value;

    const dialogRef = this.dialog.open(TimeRangeDialogComponent, {
      panelClass: 'bw-dialog-panel',
      width: '400px',
      data: { start: timeRange.fromTime, end: timeRange.toTime },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.timeRanges.at(index).patchValue(result);
      }
    });
  }

  getThumbLeft(value: number): number {
    const min = 0;
    const max = 1439;
    return ((value - min) / (max - min)) * 100;
  }

  addPresetRange(preset: { from: number; to: number; label: string }) {
    const exists = this.timeRanges.controls.some(ctrl =>
      ctrl.value.fromTime === preset.from && ctrl.value.toTime === preset.to
    );
  
    if (!exists) {
          // Add the new time range
        this.timeRanges.push(
          new FormGroup({ 
              fromTime: new FormControl(preset.from), 
              toTime: new FormControl(preset.to)
            }
          )
        );
    }
  }

  chipTooltipDisabled = false;

  onChipHover(tooltip: MatTooltip) {
    // Reset by disabling and reenabling
    this.chipTooltipDisabled = true;
    this.zone.run(() => {
      this.chipTooltipDisabled = false;
      tooltip.show();
    }, 100); // Small delay to reset lifecycle
  }
}
